import { useEffect, useState, lazy, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  NavLink,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { AnimatePresence } from "framer-motion";

import "./housestyle.css";
import logo from "./Media/logo.png";
import logoS from "./Media/logo_s.png";
import api from "./API/Api";

import Home from "./Home/Home";
import Apply from "./Apply/Apply";
import Blogs from "./Blogs/Blogs";
import BlogPost from "./Blogs/BlogPost";
import Rules from "./Rules/Rules";
import StaffPage from "./Staff/Staff";
import GiveawayPage from "./Giveaways/Giveaways";
import Ranks from "./Ranks/Ranks";
import SeasonInfo from "./SeasonInfo/SeasonInfo";
import Wiki from "./Wiki/Wiki";
import { PageWrapper, FadeIn } from "./components/Animations";

const AdminPage = lazy(() => import('./Admin/Admin'));

function DiscountBanner() {
  const [sale, setSale] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api("getsale").then((res) => {
      if (res.isSuccess && res.data) setSale(res.data);
    });
  }, []);

  if (!sale?.active) return null;

  function copy() {
    navigator.clipboard.writeText(sale.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="discount-banner">
      {sale.label && <span className="discount-banner-text">{sale.label}</span>}
      <button className="discount-code-chip" onClick={copy}>
        {sale.code}
        <span className="discount-code-chip-copy">
          {copied ? "Copied!" : "Copy"}
        </span>
      </button>
        {/* <span className="discount-banner-label">~ Sale on ranks use code</span> */}
    </div>
  );
}

function ComingSoon({ title }) {
  return (
    <div style={{ padding: "4rem 2rem", textAlign: "center" }}>
      <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🚧</div>
      <h2 style={{ fontSize: "var(--t-2xl)", marginBottom: "0.5rem" }}>
        {title}
      </h2>
      <p style={{ color: "var(--text-muted)", fontSize: "var(--t-sm)" }}>
        This page is coming soon.
      </p>
    </div>
  );
}

const NAV = [
  { path: "/", label: "Home" },
  { path: "/giveaways", label: "Giveaways" },
  { path: "/blogs", label: "Blogs" },
  { path: "/our-staff", label: "Staff" },
  { path: "/rules", label: "Rules" },
  { path: "/apply", label: "Apply" },
  { path: "/ranks", label: "Ranks" },
  { path: "/SeasonInfo", label: "Season Info" },
];

function Navbar() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [ign, setIgn] = useState("");

  useEffect(() => {
    const saved = localStorage.getItem("mc_user");
    if (saved) setUser(JSON.parse(saved));
  }, []);

  function handleLogin(e) {
    e.preventDefault();
    if (!ign) return;
    const userData = { ign: ign, avatar: `https://mc-heads.net/avatar/${ign}/80` };
    localStorage.setItem("mc_user", JSON.stringify(userData));
    setUser(userData);
    setShowLogin(false);
    setIgn("");
  }

  function handleLogout() {
    localStorage.removeItem("mc_user");
    setUser(null);
  }

  return (
    <>
      <FadeIn y={-20} duration={0.6} className="navbar">
        <div
          className="navbar-brand"
          onClick={() => navigate("/")}
          style={{ cursor: "pointer" }}
        >
          <img src={logo} alt="SimpleMC" className="navbar-logo-full" />
          <img src={logoS} alt="SimpleMC" className="navbar-logo-s" />
        </div>

        <div className="navbar-links">
          {NAV.map(({ path, label }) => (
            <NavLink
              key={path}
              to={path}
              end={path === "/"}
              className={({ isActive }) =>
                `nav-link${isActive ? " active" : ""}`
              }
            >
              {label}
            </NavLink>
          ))}
          <NavLink to="/wiki" className="nav-wiki-link">
            Wiki
          </NavLink>
        </div>

        <div className="nav-actions desktop-only">
          {user ? (
            <div className="nav-user" onClick={handleLogout} title="Click to logout">
              <img src={user.avatar} alt="Avatar" className="nav-user-head" />
              <span className="nav-user-name">{user.ign}</span>
            </div>
          ) : (
            <button className="nav-login" onClick={() => setShowLogin(true)}>
              Login
            </button>
          )}
          <button
            onClick={() => window.open("https://discord.com/invite/simplemc", "_blank")}
            className="nav-discord nav-discord-desktop"
          >
            Join Discord
          </button>
        </div>

        <button
          className="nav-hamburger"
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span />
          <span />
          <span />
        </button>
      </FadeIn>

      {/* Login Modal */}
      {showLogin && (
        <div className="login-modal-overlay" onClick={() => setShowLogin(false)}>
          <div className="login-modal" onClick={e => e.stopPropagation()}>
            <div className="login-modal-header">
              <div className="login-avatar-preview">
                <img 
                  src={`https://mc-heads.net/avatar/${ign || 'steve'}/100`} 
                  alt="Preview" 
                />
              </div>
              <h2>Login with Minecraft</h2>
              <p>Verify your profile to unlock special features.</p>
            </div>
            <form onSubmit={handleLogin}>
              <div className="login-input-group">
                <input 
                  type="text" 
                  placeholder="Minecraft Username" 
                  value={ign}
                  onChange={e => setIgn(e.target.value)}
                  autoFocus
                />
              </div>
              <button type="submit" className="btn-primary login-submit">Connect Account</button>
            </form>
            <button className="login-cancel" onClick={() => setShowLogin(false)}>Maybe Later</button>
          </div>
        </div>
      )}

      {menuOpen && (
        <div className="nav-mobile-drawer">
          {user && (
            <div className="nav-mobile-user">
              <img src={user.avatar} alt="Avatar" />
              <span>{user.ign}</span>
              <button onClick={handleLogout} className="btn-ghost" style={{ fontSize: '10px' }}>Logout</button>
            </div>
          )}
          {NAV.map(({ path, label }) => (
            <NavLink
              key={path}
              to={path}
              end={path === "/"}
              className={({ isActive }) =>
                `nav-mobile-link${isActive ? " active" : ""}`
              }
              onClick={() => setMenuOpen(false)}
            >
              {label}
            </NavLink>
          ))}
          {!user && (
            <button className="nav-mobile-link" onClick={() => { setShowLogin(true); setMenuOpen(false); }}>
              Login
            </button>
          )}
          <button
            onClick={() => { window.open("https://discord.com/invite/simplemc", "_blank"); setMenuOpen(false); }}
            className="nav-discord nav-discord-drawer"
          >
            Join Discord
          </button>
        </div>
      )}
    </>
  );
}

function Layout() {
  const location = useLocation();
  useEffect(() => {
    const last = localStorage.getItem("lastVisit");
    const now = Date.now();
    if (!last || now - last > 300000) {
      localStorage.setItem("lastVisit", now);
      api("recordvisit");
    }
  }, []);

  return (
    <div>
      <Navbar />
      <DiscountBanner />
      <main>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageWrapper><Home /></PageWrapper>} />
          <Route path="/SeasonInfo" element={<PageWrapper><SeasonInfo /></PageWrapper>} />
          <Route path="/ranks" element={<PageWrapper><Ranks /></PageWrapper>} />
          <Route path="/apply" element={<PageWrapper><Apply /></PageWrapper>} />
          <Route path="/rules" element={<PageWrapper><Rules /></PageWrapper>} />
          <Route path="/blogs" element={<PageWrapper><Blogs /></PageWrapper>} />
          <Route path="/blogs/:id" element={<PageWrapper><BlogPost /></PageWrapper>} />
          <Route path="/our-staff" element={<PageWrapper><StaffPage /></PageWrapper>} />
          <Route path="/giveaways" element={<PageWrapper><GiveawayPage /></PageWrapper>} />
          <Route path="*" element={<PageWrapper><ComingSoon title="Page Not Found" /></PageWrapper>} />
          </Routes>
        </AnimatePresence>
      </main>
      <footer className="site-footer">
        <div className="footer-brand">SimpleMC</div>
        <p>Survival SMP · simplemcnetwork.net · Season 1</p>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin" element={<Navigate to="/wiki/admin" replace />} />
        {AdminPage && (
          <Route
            path="/wiki/admin"
            element={<Suspense fallback={null}><AdminPage /></Suspense>}
          />
        )}
        <Route path="/wiki/*" element={<Wiki />} />
        <Route path="/*" element={<Layout />} />
      </Routes>
    </BrowserRouter>
  );
}
